import React, { useEffect, useState } from 'react'
import {
  Box,
  InputBase,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  useTheme,
  useMediaQuery,
  InputAdornment,
  InputLabel
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useSelector } from 'react-redux'

import MachineListAPIs from '../../utilities/api/machine/AdminMachineList'
import EquipmentListAPIs from '../../utilities/api/equipment/AdminEquipmentList'
import InventoryListAPIs from '../../utilities/api/inventory/AdminInventoryList'
import LayoutImageManager from './Popups/LayoutImageManager'
import { KeyboardArrowDown } from '@mui/icons-material'

const tagsList = [
  'Free',
  'Issue',
  'Purchase',
  'Exhaustible',
  'NonExhaustible',
  'InLabOnly',
  'HighValue'
]

const SearchBar = ({
  activeTab,
  searchQuery,
  setSearchQuery,
  category,
  setCategory,
  group,
  setGroup,
  tag,
  setTag
}) => {
  const [categories, setCategories] = useState([])
  const [groups, setGroups] = useState([])
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const user = useSelector((state) => state.user.user)
  const tokens = {
    refresh_token: user.refresh,
    access_token: user.access
  }

  // Effect to handle API calls and reset filters when activeTab changes
  useEffect(() => {
    // Reset filters when the activeTab changes
    setCategory([])
    setGroup([])
    setTag([])

    setLoading(true)
    setError(null)
    let apiCall
    switch (activeTab) {
      case 0:
        apiCall = MachineListAPIs.MachineListGet()
        break
      case 1:
        apiCall = EquipmentListAPIs.EquipmentListGet()
        break
      case 2:
        apiCall = InventoryListAPIs.InventoryListGet(tokens)
        break
      default:
        break
    }

    if (apiCall) {
      apiCall
        .then((result) => {
          const uniqueCategories = Array.from(new Set(result.map((item) => item.category))).filter(
            Boolean
          )
          setCategories(uniqueCategories)

          const uniqueGroups = Array.from(new Set(result.map((item) => item.group))).filter(Boolean)
          setGroups(uniqueGroups)

          const allTags = result.reduce((acc, item) => {
            const tagsArray = [item.tag_1, item.tag_2, item.tag_3, item.tag_4].filter(Boolean)
            return acc.concat(tagsArray)
          }, [])
          const uniqueTags = Array.from(new Set(allTags)).filter(Boolean)
          setTags(uniqueTags)
          setLoading(false)
        })
        .catch((error) => {
          setError(error)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [activeTab, user, setCategory, setGroup, setTag])

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
  }

  const handleGroupChange = (event) => {
    setGroup(event.target.value)
  }

  const handleTagChange = (event) => {
    setTag(event.target.value)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSearchSubmit()
    }
  }

  const handleSearchSubmit = () => {
    console.log(`Searching for: ${searchQuery} in category: ${category}`)
    // Implement actual search functionality here
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box
      component="form"
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: isMobile ? 1 : 0,
        width: '100%',
        padding: theme.spacing(1),
        margin: 'none',
        marginBlock: '1rem',
        boxSizing: 'border-box'
      }}
    >
      <FormControl sx={{ minWidth: 120 }}>
        <InputLabel sx={{ marginLeft: '18px', lineHeight: '1.2rem' }}>Select Categories</InputLabel>
        <Select
          value={category}
          onChange={handleCategoryChange}
          sx={{
            mr: activeTab === 2 ? 20 : 45.5,
            height: '3rem',
            width: activeTab === 2 ? '90%' : '50%',
            ml: '1rem',
            textAlign: 'left',
            borderRadius: '8px'
          }}
          multiple
          label="Select Categories"
          IconComponent={KeyboardArrowDown}
          disabled={categories?.length === 0}
        >
          {categories.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {activeTab === 2 && (
        <>
          <FormControl sx={{ minWidth: 110 }}>
            <InputLabel sx={{ marginLeft: '18px', lineHeight: '1.2rem' }}>Group</InputLabel>
            <Select
              value={group}
              onChange={handleGroupChange}
              sx={{ mr: 5, height: '3rem', width: '80%', ml: '1rem', textAlign: 'left' }}
              // multiple
              label="Group"
              IconComponent={KeyboardArrowDown}
              disabled={groups?.length === 0}
            >
              {groups.map((grp) => (
                <MenuItem key={grp} value={grp}>
                  {grp}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 80 }}>
            <InputLabel sx={{ marginLeft: '18px', lineHeight: '1.2rem' }}>Tags</InputLabel>
            <Select
              value={tag}
              onChange={handleTagChange}
              sx={{ mr: 5, height: '3rem', width: '80%', ml: '1rem', textAlign: 'left' }}
              // multiple
              label="Tags"
              IconComponent={KeyboardArrowDown}
              disabled={tags?.length === 0}
            >
              {tags.map((tg) => (
                <MenuItem key={tg} value={tg}>
                  {tg}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      <InputBase
        sx={{
          ml: 50,
          mr: 1,
          flex: 1,
          height: '3rem',
          padding: '0',
          border: '#E6E6E6 solid 1px',
          borderRadius: '10px',
          bgcolor: '#FAFAFA',
          '& .MuiInputBase-input': {
            paddingLeft: '.1rem'
          }
        }}
        placeholder="Search…"
        inputProps={{ 'aria-label': 'search' }}
        value={searchQuery}
        onChange={handleSearchChange}
        startAdornment={
          <InputAdornment position="start">
            <IconButton sx={{ paddingLeft: '10px' }} aria-label="search">
              <SearchIcon />
            </IconButton>
          </InputAdornment>
        }
      />
      {activeTab === 3 && (
        <LayoutImageManager /> // Integrate the new component
      )}
    </Box>
  )
}

export default SearchBar
